/**
 * Minimal inline sparkline for the stat tile (S4) — a single 2px polyline
 * with no axes/legend/tooltip (dataviz skill: a sparkline is a "hero number"
 * decoration, not a standalone chart). Neutral by default; tints to the
 * active threshold severity so it never introduces a second, uncoordinated
 * color language — status colors are reserved for severity (UI Quality §1).
 */
export type StatSparklinePoint = { time: unknown; value: number };

const WIDTH = 96;
const HEIGHT = 28;

const SEVERITY_STROKE: Record<'none' | 'warning' | 'critical', string> = {
  none: 'text-muted-foreground/70',
  warning: 'text-warning',
  critical: 'text-red-500',
};

export function StatSparkline({
  points,
  severity,
}: {
  points: StatSparklinePoint[];
  severity: 'none' | 'warning' | 'critical';
}) {
  if (points.length < 2) return null;

  const values = points.map((p) => p.value);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  const stepX = WIDTH / (points.length - 1);
  const coords = values
    .map((v, i) => {
      const x = i * stepX;
      const y = HEIGHT - ((v - min) / range) * (HEIGHT - 4) - 2;
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(' ');

  return (
    <svg
      width={WIDTH}
      height={HEIGHT}
      viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
      className={SEVERITY_STROKE[severity]}
      role="img"
      aria-label="Trend sparkline"
      data-testid="stat-tile-sparkline"
    >
      <polyline
        points={coords}
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
